import axios, { AxiosError } from "axios";
import { getToken, removeToken, removeUser, SERVER_URL } from "./Process";

// 401 from these means "wrong password/code", not "session expired"
const AUTH_ENDPOINTS = [
  "/login",
  "/verify-2fa",
  "/resend-2fa-code",
  "/forgot-password",
  "/reset-password",
];
const isAuthEndpoint = (url = "") => AUTH_ENDPOINTS.some((e) => url.split("?")[0].endsWith(e));

let redirectingToSignIn = false;

/**
 * Token rejected by the API: clear the session and go to sign-in with a
 * "session expired" notice, remembering the page to return to afterwards.
 */
const handleExpiredSession = () => {
  if (redirectingToSignIn) return; // several requests can fail at once
  redirectingToSignIn = true;
  removeToken();
  removeUser();

  const base = process.env.PUBLIC_URL || "";
  const here = window.location.pathname.slice(base.length) + window.location.search;
  const params = new URLSearchParams({ reason: "expired" });
  if (here.startsWith("/") && !here.startsWith("/sign-in") && here !== "/") {
    params.set("next", here);
  }
  window.location.replace(`${base}/sign-in?${params.toString()}`);
};

const apiClient = axios.create({
  baseURL: SERVER_URL,
});

apiClient.defaults.headers.common['Content-Type'] = 'application/json';
apiClient.defaults.headers.common['Accept'] = 'application/json';

// Add request interceptor to include the Bearer token
apiClient.interceptors.request.use(
  (config) => {
    const token = getToken(); // Get the token
    if (token) {
      config.headers.Authorization = `Bearer ${token}`; // Add the token to headers
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    let errorMessage = "An unexpected error occurred.";

    if (error.response) {
      // Server responded with a status other than 2xx (no headers logged: they hold the token)
      console.error("API error:", error.response.status, error.config?.url);
      const serverMessage = (error.response.data as { message?: string } | undefined)?.message;

      switch (error.response.status) {
        case 400:
          errorMessage = serverMessage || "Bad Request. Please check your input.";
          break;
        case 401: {
          const sentToken = !!error.config?.headers?.Authorization;
          if (isAuthEndpoint(error.config?.url)) {
            // wrong password / code: show the API's message on the form
            errorMessage = serverMessage || "Incorrect details. Please check and try again.";
          } else if (sentToken) {
            errorMessage = "Your session has expired. Please sign in again.";
            handleExpiredSession();
          } else {
            // not signed in at all (e.g. opening a page directly); the route guard redirects quietly
            errorMessage = "Please sign in to continue.";
          }
          break;
        }
        case 403:
          errorMessage =
            serverMessage || "You don't have permission to do this.";
          break;
        case 404:
          errorMessage = "Resource not found. Please check the URL.";
          break;
        case 422:
          const data = error.response.data as {
            message?: string;
            errors?: Record<string, string[]>;
          };

          if (data.errors) {
            errorMessage = Object.values(data.errors).flat().join(", ");
          } else {
            errorMessage = data.message || "Validation error occurred.";
          }
          break;
        case 429:
          errorMessage = "Too many attempts. Please wait a minute and try again.";
          break;
        case 500:
          errorMessage = "Server error occurred. Please try again later.";
          break;
        default:
          errorMessage = `An error occurred: ${error.response.status}`;
      }
    } else if (error.request) {
      // No response received
      console.error("Network error:", error.message, error.config?.url);
      errorMessage = "Network error. Please check your connection.";
    } else {
      // Something else happened
      console.error("Unexpected error:", error.message);
      errorMessage = "Unexpected error. Please try again later.";
    }

    // Optionally, you can return a custom error object or message
    return Promise.reject(errorMessage);
  }
);

export default apiClient;
